// app.storiz.ma/couvertures — the PUBLIC cover catalog (owner, 2026-10-17; sketch
// approved « OK sketch »). No login, phone first, Storiz colours.
//
// · Category tabs = the admin's own categories; ?cat=mariage opens on one.
// · 12 most popular covers per category, then « Voir plus ». Small images (400 px,
//   storage CDN) drawn by the SAME CoverThumbnail as the admin library: colour +
//   artwork + the template's title and year. Rendered only when scrolled near.
// · Tap → the cover as a 3D book (the preview page's book) + « Je choisis ce
//   modèle » → WhatsApp, the storefront's weighted numbers, text pre-filled.
// · Shows NOTHING internal: the slug rides only in the WhatsApp text.
// · OFF (app_settings.cover_catalog_enabled): clients see « bientôt »; a signed-in
//   team member sees the page with a banner, to check it before switching on.
import { useEffect, useRef, useState } from 'react';
import { catalogApi, type CatalogCover, type CatalogPage } from '@/lib/api';
import { asThumbnailCover, catFromSearch, chooseMessage, pickNumber, waLink } from '@/lib/coverCatalogClient';
import { CoverThumbnail } from '@/components/CoverThumbnail';

const C = { bg: '#FAF7F2', card: '#FFFFFF', soft: '#F4EFE7', ink: '#2E2418', muted: '#6B5D4F', line: '#E8E0D2', gold: '#C9A04C', wa: '#157A43' };
const SERIF = "'Fraunces', Georgia, serif";
/** One weighted draw (an event handler's job, never a render's). The app's fixed number if the pool is empty. */
function drawNumber(pool: Array<{ n: string; w: number }>): string {
  return pickNumber(pool, Math.random()) ?? '212620066907';
}

/** Renders its child only once it is near the screen — 12 covers never load at once on 4G. */
function NearScreen({ children, color }: { children: React.ReactNode; color: string | null }) {
  const ref = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(() => typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    const el = ref.current;
    if (!el || near) return;
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setNear(true); io.disconnect(); } }, { rootMargin: '300px' });
    io.observe(el);
    return () => io.disconnect();
  }, [near]);
  return (
    <div ref={ref} style={{ aspectRatio: '2 / 3', background: color ?? C.soft, borderRadius: 6, overflow: 'hidden' }}>
      {near ? children : null}
    </div>
  );
}

function Book3D({ cover }: { cover: CatalogCover }) {
  return (
    <div className="pv-book3d" style={{ display: 'flex', justifyContent: 'center' }}>
      <div className="pv-book3d-inner" style={{ height: 'min(46vh, 420px)', aspectRatio: '2 / 3', boxShadow: '-22px 34px 50px rgba(46,36,24,0.35), -6px 12px 20px rgba(46,36,24,0.25)' }}>
        <div className="pv-cover-spine" style={{ backgroundColor: cover.dominant_color ?? '#2A2117', filter: 'brightness(0.8)' }} />
        <div className="pv-cover-edge" />
        <div style={{ position: 'absolute', inset: 0, borderRadius: '3px 12px 12px 3px', overflow: 'hidden', backfaceVisibility: 'hidden' }}>
          <CoverThumbnail cover={asThumbnailCover(cover, true)} />
        </div>
        <div className="pv-cover-sheen" />
      </div>
    </div>
  );
}

export default function CoverCatalogPage() {
  const [data, setData] = useState<CatalogPage | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'off' | 'error'>('loading');
  const [cat, setCat] = useState<string | null>(null);
  const [covers, setCovers] = useState<CatalogCover[]>([]);
  const [more, setMore] = useState(false);
  const [busyMore, setBusyMore] = useState(false);
  const [open, setOpen] = useState<CatalogCover | null>(null);
  const [pool, setPool] = useState<Array<{ n: string; w: number }>>([]);
  const preview = Boolean(data?.preview);

  const load = async (nextCat: string | null, offset: number) => {
    try {
      let page = await catalogApi.list(nextCat, offset);
      if (!page.enabled) {
        // OFF: the team still sees it (signed in); a client sees « bientôt ».
        try { page = await catalogApi.preview(nextCat, offset); } catch { setState('off'); return null; }
      }
      return page;
    } catch {
      setState('error');
      return null;
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      const first = await load(null, 0);
      if (!alive || !first) return;
      const wanted = catFromSearch(window.location.search, (first.categories ?? []).map((t) => t.slug));
      const page = wanted ? await load(wanted, 0) : first;
      if (!alive || !page) return;
      setData(page); setCat(page.cat ?? null); setCovers(page.covers ?? []); setMore(Boolean(page.more)); setState('ready');
    })();
    catalogApi.whatsapp().then((r) => setPool(r.pool ?? [])).catch(() => setPool([]));
    return () => { alive = false; };
  }, []);

  const choose = async (slug: string | null) => {
    setCat(slug); setCovers([]); setMore(false);
    const url = new URL(window.location.href);
    if (slug) url.searchParams.set('cat', slug); else url.searchParams.delete('cat');
    window.history.replaceState(null, '', url.toString());
    const page = await load(slug, 0);
    if (page) { setData(page); setCovers(page.covers ?? []); setMore(Boolean(page.more)); }
  };

  const loadMore = async () => {
    setBusyMore(true);
    const page = await load(cat, covers.length);
    setBusyMore(false);
    if (page) { setCovers((prev) => [...prev, ...(page.covers ?? [])]); setMore(Boolean(page.more)); }
  };

  const [number, setNumber] = useState<string | null>(null);
  const openCover = (c: CatalogCover) => {
    setOpen(c);
    // One weighted draw per opened cover — the storefront's rule.
    setNumber(drawNumber(pool));
    if (!preview) void catalogApi.event(c.id, 'view');
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const tabs = data?.categories ?? [];

  if (state === 'off' || state === 'error') {
    return (
      <div style={{ minHeight: '100vh', background: C.bg, color: C.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
        <div>
          <p style={{ fontFamily: SERIF, fontSize: 26, fontWeight: 600, margin: 0 }}>Storiz</p>
          <p style={{ marginTop: 12, color: C.muted, fontSize: 15 }}>{state === 'off' ? 'Notre catalogue de couvertures arrive bientôt.' : 'Le catalogue ne peut pas s’afficher pour le moment. Réessayez dans un instant.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.ink }}>
      {preview && (
        <div role="status" style={{ background: '#FDF3DC', color: '#6B4A0E', fontSize: 13, padding: '8px 16px', textAlign: 'center' }}>
          Aperçu équipe — le catalogue est désactivé : les clients ne le voient pas encore.
        </div>
      )}
      <header style={{ borderBottom: `1px solid ${C.line}`, background: C.card }}>
        <div style={{ maxWidth: 1160, margin: '0 auto', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: SERIF, fontWeight: 600, fontSize: 24 }}>Storiz</span>
          <span style={{ fontSize: 13, color: C.muted }}>Livraison gratuite partout au Maroc · Paiement à la livraison</span>
        </div>
      </header>

      <main style={{ maxWidth: 1160, margin: '0 auto', padding: '28px 20px 56px' }}>
        <h1 style={{ margin: 0, fontFamily: SERIF, fontWeight: 500, fontSize: 'clamp(30px, 5vw, 46px)', lineHeight: 1.1 }}>Choisissez votre couverture</h1>
        <p style={{ margin: '10px 0 0', fontSize: 'clamp(14.5px, 1.6vw, 17px)', lineHeight: 1.5, color: C.muted, maxWidth: 620 }}>
          Votre titre et vos dates sont ajoutés pour vous. Touchez un modèle pour le voir en 3D.
        </p>

        <nav aria-label="Catégories" style={{ marginTop: 22, display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6 }}>
          {[{ slug: null as string | null, label: 'Tous', icon: null as string | null }, ...tabs].map((t) => {
            const on = (t.slug ?? null) === (cat ?? null);
            return (
              <button key={t.slug ?? 'all'} type="button" onClick={() => void choose(t.slug)} aria-pressed={on}
                style={{ flex: 'none', minHeight: 44, padding: '0 16px', borderRadius: 999, border: `1px solid ${on ? C.ink : C.line}`, background: on ? C.ink : C.card, color: on ? C.bg : C.ink, fontSize: 14.5, fontWeight: on ? 600 : 500, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {t.icon ? <span aria-hidden style={{ marginRight: 6 }}>{t.icon}</span> : null}{t.label}
              </button>
            );
          })}
        </nav>

        {state === 'loading' ? (
          <p style={{ marginTop: 28, color: C.muted }}>Chargement…</p>
        ) : covers.length === 0 ? (
          <p style={{ marginTop: 28, color: C.muted }}>Aucune couverture dans cette catégorie pour le moment.</p>
        ) : (
          <section style={{ marginTop: 24, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(40vw, 200px), 1fr))', gap: '22px 16px' }}>
            {covers.map((c) => (
              <button key={c.id} type="button" onClick={() => openCover(c)} aria-label={`Voir la couverture ${c.name} en 3D`}
                style={{ display: 'flex', flexDirection: 'column', gap: 8, background: 'none', border: 0, padding: 0, textAlign: 'left', cursor: 'pointer', color: C.ink }}>
                <div style={{ width: '100%', boxShadow: '0 1px 2px rgba(46,36,24,0.12), 0 10px 22px -12px rgba(46,36,24,0.4)', borderRadius: 6 }}>
                  <NearScreen color={c.dominant_color}><CoverThumbnail cover={asThumbnailCover(c)} /></NearScreen>
                </div>
                <span style={{ fontSize: 15, fontWeight: 500 }}>{c.name}</span>
              </button>
            ))}
          </section>
        )}

        {more && (
          <div style={{ marginTop: 32, display: 'flex', justifyContent: 'center' }}>
            <button type="button" onClick={() => void loadMore()} disabled={busyMore}
              style={{ minHeight: 48, padding: '0 28px', borderRadius: 999, border: `1px solid ${C.gold}`, background: C.card, color: C.ink, fontSize: 15.5, fontWeight: 600, cursor: 'pointer', opacity: busyMore ? 0.6 : 1 }}>
              {busyMore ? 'Chargement…' : 'Voir plus de modèles'}
            </button>
          </div>
        )}
      </main>

      {open && (
        <div role="dialog" aria-modal="true" aria-label={`Couverture ${open.name}`}
          style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(46,36,24,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setOpen(null)}>
          <div onClick={(e) => e.stopPropagation()} className="catalog-sheet"
            style={{ background: C.card, width: 'min(980px, 100vw)', maxHeight: '100dvh', overflow: 'auto', borderRadius: 22, display: 'flex', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 320px', background: C.soft, padding: '40px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 360 }}>
              <Book3D cover={open} />
            </div>
            <div style={{ flex: '1 1 300px', padding: '22px 24px 26px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setOpen(null)} aria-label="Fermer"
                  style={{ width: 44, height: 44, borderRadius: 999, border: 0, background: C.soft, color: C.ink, cursor: 'pointer', fontSize: 18 }}>✕</button>
              </div>
              <h2 style={{ margin: 0, fontFamily: SERIF, fontWeight: 500, fontSize: 34, lineHeight: 1.08 }}>{open.name}</h2>
              <p style={{ margin: 0, fontSize: 16, lineHeight: 1.5, color: C.muted }}>Titre et date personnalisés avec vos infos.</p>
              <div style={{ flex: 1 }} />
              <a href={number ? waLink(number, chooseMessage(open.name, open.slug)) : '#'} target="_blank" rel="noopener noreferrer"
                onClick={() => { if (!preview) void catalogApi.event(open.id, 'choose'); }}
                style={{ minHeight: 54, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, borderRadius: 14, background: C.wa, color: '#FFFFFF', fontSize: 16.5, fontWeight: 600, textDecoration: 'none' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20.5 11.6a8.4 8.4 0 0 1-12.4 7.4L3.5 20.4l1.5-4.4A8.4 8.4 0 1 1 20.5 11.6z" /></svg>
                Je choisis ce modèle
              </a>
              <p style={{ margin: 0, fontSize: 13, color: C.muted, textAlign: 'center' }}>Ouvre WhatsApp avec votre choix déjà écrit.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
